/**
 * The account's day-by-day record from its equity curve: each day's closing value and change,
 * how many days were up or down, and the worst peak-to-trough drop. Built from the summed
 * portfolio series (hourly bars), so a deposit or withdrawal shows as a move too — the UI says
 * so. Pure (tested in __tests__/home-performance.test.ts).
 */

export interface DayRecord {
  /** Epoch ms of the day's UTC midnight. */
  dayMs: number
  /** Account value at the day's last bar. */
  close: number
  /** Change against the previous day's close (USD); null on the first day. */
  change: number | null
  /** `change` as a percent of the previous close. */
  changePercent: number | null
}

export interface PerformanceSummary {
  days: DayRecord[]
  upDays: number
  downDays: number
  /** Largest peak-to-trough fall over the window, as a positive percent (0 when none). */
  maxDrawdownPercent: number
  best: DayRecord | null
  worst: DayRecord | null
}

const DAY_MS = 86_400_000

export function summarizePerformance(
  ticks: readonly (string | number)[],
  values: readonly number[],
  days: number,
  now: number
): PerformanceSummary {
  const closes = new Map<number, number>()
  const length = Math.min(ticks.length, values.length)
  for (let i = 0; i < length; i++) {
    const raw = Number(ticks[i])
    // Series ticks are unix seconds; accept ms too.
    const ms = raw < 1e12 ? raw * 1000 : raw
    const value = values[i]
    if (!Number.isFinite(ms) || !Number.isFinite(value)) continue
    closes.set(Math.floor(ms / DAY_MS) * DAY_MS, value)
  }

  const lastDay = Math.floor(now / DAY_MS) * DAY_MS
  const firstDay = lastDay - (days - 1) * DAY_MS
  const records: DayRecord[] = []
  let previous: number | null = null
  // Seed the first change from the day before the window when the series has it.
  previous = closes.get(firstDay - DAY_MS) ?? null
  for (let day = firstDay; day <= lastDay; day += DAY_MS) {
    const close = closes.get(day)
    if (close === undefined) continue
    const change = previous !== null ? close - previous : null
    records.push({
      dayMs: day,
      close,
      change,
      changePercent: change !== null && previous ? (change / previous) * 100 : null,
    })
    previous = close
  }

  let peak = -Infinity
  let maxDrawdown = 0
  for (const record of records) {
    peak = Math.max(peak, record.close)
    if (peak > 0) maxDrawdown = Math.max(maxDrawdown, (peak - record.close) / peak)
  }
  const changed = records.filter((record) => record.change !== null)
  const byChange = [...changed].sort((a, b) => (b.change ?? 0) - (a.change ?? 0))

  return {
    days: records,
    upDays: changed.filter((record) => (record.change ?? 0) > 0).length,
    downDays: changed.filter((record) => (record.change ?? 0) < 0).length,
    maxDrawdownPercent: maxDrawdown * 100,
    best: byChange[0] && (byChange[0].change ?? 0) > 0 ? byChange[0] : null,
    worst:
      byChange.length > 0 && (byChange[byChange.length - 1].change ?? 0) < 0
        ? byChange[byChange.length - 1]
        : null,
  }
}
